import {
  Autocomplete,
  Box,
  Button,
  Divider,
  MenuItem,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback } from "react";
import Modal, { CommonModalProps, ModalContext } from "..";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddIcon from "@mui/icons-material/Add";
import UserGroupChip from "../../Misc/Chips/UserGroupChip/user-group-chip";
import { DbUserInfos } from "../../../Core/Api/Types/db-user-infos";
import { produce } from "immer";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";
import { cacheService, orderService, userService } from "../../../Core/Api/services";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import FormInputEmail from "../../Forms/FormInputEmail/form-input-email";
import { GetFieldIsValidAndErrorMessage } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import ModalAddGroup, { ModalAddGroupElementProps } from "../ModalAddGroup/modal-add-group";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalAddUserElementProps = {
  onClose?: (props: CloseClbProps) => void;
};
export type ModalAddUserProps = CommonModalProps<ModalAddUserElementProps>;

const ModalAddUser = React.forwardRef<any, ModalAddUserProps>((props, ref) => {
  const { closeModal, onClose, ...modalProps } = props;
  const [newUser, setNewUser] = React.useState<DbUserInfos>(
    new DbUserInfos({ enabled: true, isSynced: true, deleted: false, lastPasswordChange: undefined })
  );
  const [groupList, setGroupList] = React.useState<GroupAdvancedInfos[]>([]);
  const [roleList, setRoleList] = React.useState<DbUserGroupRoleInfos[]>([]);
  const [groupSelected, setGroupSelected] = React.useState<GroupAdvancedInfos>();
  const [customers, setCustomers] = React.useState<DbCustomerInfos[]>([]);
  const [customersSelected, setCustomersSelected] = React.useState<DbCustomerInfos[]>([]);
  const [loginMethod, setLoginMethod] = React.useState<"email" | "name" | "manual" | string>("email");
  const enqueueSnackbar = useCustomSnackbar();
  const isEmailValid = !useAppSelector(GetFieldIsValidAndErrorMessage("id", "input-mail"));
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { t } = useTranslation();

  const loginSuffix = cacheService.GetAuthLoginResponse()?.loginSuffix;

  const handleOpenModalNewGroup = React.useCallback(() => {
    if (!openModal) return;
    const onClose: ModalAddGroupElementProps["onClose"] = (data) => {};

    openModal<ModalAddGroupElementProps>(ModalAddGroup, {
      onClose,
    });
  }, [openModal]);

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  React.useEffect(() => {
    setNewUser(
      produce((draft) => {
        switch (loginMethod) {
          case "manual":
            draft!.login = "";
            break;
          case "email":
            draft!.login = draft.email || "";
            break;
          case "name":
            draft!.login = (draft.firstName || "") + "." + (draft.lastName || "") + loginSuffix;
            break;
        }
      })
    );
  }, [loginMethod, loginSuffix, newUser.firstName, newUser.lastName, newUser.email]);

  const handleConfirmClick = useCallback(() => {
    if (newUser) {
      userService.CreateUser({ userCustomersLinks: customersSelected, userInfos: newUser }).then(function (response) {
        if (response.success) {
          enqueueSnackbar(t("alert:success.newUserCreated"), "success");
          onClose && onClose({ confirm: true });
        } else enqueueSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
      });
    }
  }, [newUser, customersSelected, loginMethod]);

  React.useEffect(() => {
    orderService.GetCustomersList().then(function (response) {
      if (response.success) setCustomers(response.customers || []);
    });

    userService.GetGroupsList().then(function (response) {
      if (response.success && response.groups) {
        response.groups =
          response.groups?.filter(
            (x) => x.group?.enabled && x.group.name !== "deleted" && x.group.name !== "Deleted Account"
          ) || [];
        setGroupList(response.groups);
      }
    });

    userService.GetGroupRolesList().then(function (response) {
      if (response.success) {
        setRoleList(response.groupRoles || []);
      }
    });
  }, []);

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.newUser")}
      reference={ref}
      closeModal={handleCancelClick}
      width={680}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={
              newUser.firstName === null ||
              newUser.firstName === "" ||
              newUser.lastName === null ||
              newUser.lastName === "" ||
              newUser.email === null ||
              newUser.email === "" ||
              newUser.login === "" ||
              newUser.login === null ||
              newUser.groupId === 0 ||
              newUser.groupId === undefined ||
              !isEmailValid
            }
          >
            {t("common:create")}
          </Button>
        </Stack>
      }
    >
      <Typography variant="h5" mb={2}>
        {t("common:generalInformation")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item md={6}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel shrink htmlFor="firstname">
              {t("common:firstName")}
            </InputLabel>
            <OutlinedInput
              id="firstname"
              onChange={(e) => {
                setNewUser(
                  produce((draft) => {
                    draft!.firstName = e.target.value;
                  })
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={6}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel shrink htmlFor="lastname">
              {t("common:lastName")}
            </InputLabel>
            <OutlinedInput
              id="lastname"
              onChange={(e) => {
                setNewUser(
                  produce((draft) => {
                    draft!.lastName = e.target.value;
                  })
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={6}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel shrink htmlFor="email">
              {t("common:emailAddress")}
            </InputLabel>
            <FormInputEmail
              onChange={(e) => {
                setNewUser(
                  produce((draft) => {
                    draft!.email = e.target.value;
                  })
                );
              }}
              placeholder={t("field:placeholder.emailAddress")}
              validationProps={{
                formId: "id",
                fieldId: "input-mail",
                required: false,
                initialState: true,
              }}
              stackProps={{
                spacing: 1,
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={6}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel shrink htmlFor="phone">
              {t("common:phoneNumber")}
            </InputLabel>
            <OutlinedInput
              id="phone"
              onChange={(e) => {
                setNewUser(
                  produce((draft) => {
                    draft!.phone = e.target.value;
                  })
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t("field:label.expirationDate")}
              disabled={!newUser?.enabled || newUser.enabled === null}
              value={newUser?.expireDate}
              format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
              onChange={(e) => {
                setNewUser(
                  produce((draft) => {
                    draft!.expireDate = e;
                  })
                );
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item md={6}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                onChange={(e) => {
                  setNewUser(
                    produce((draft) => {
                      draft!.enabled = e.target.checked;
                    })
                  );
                }}
                checked={newUser?.enabled || false}
              />
            }
            labelPlacement="start"
            sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
            label={t("field:label.enableUser")}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mt: 3, mb: 3 }} />

      <Typography variant="h5" mb={2}>
        {t("common:login")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item md={6}>
          <Select
            displayEmpty
            value={loginMethod}
            onChange={(e) => setLoginMethod(e.target.value)}
            IconComponent={ExpandMoreRoundedIcon}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            fullWidth
          >
            <MenuItem value={"email"} key={"Email"}>
              {t("menu:item.takeMyEmail")}
            </MenuItem>
            <MenuItem value={"name"} key={"Name"}>
              {t("menu:item.firstAndLastName")}
            </MenuItem>
            {UserHasAcl(AclTypes.aclSuperUser) && (
              <MenuItem value={"manual"} key={"Manual"}>
                {t("menu:item.manualEntry")}
              </MenuItem>
            )}
          </Select>
        </Grid>
        <Grid item md={6}>
          {loginMethod === "manual" && (
            <Stack direction="row" spacing={1} alignItems="center">
              <OutlinedInput
                id="login"
                fullWidth
                placeholder={
                  UserHasAcl(AclTypes.aclSuperUser)
                    ? t("field:placeholder.adminEnterFullLogin")
                    : t("field:placeholder.enterLogin")
                }
                onChange={(e) => {
                  setNewUser(
                    produce((draft) => {
                      draft!.login = e.target.value;
                    })
                  );
                }}
              />
              {!UserHasAcl(AclTypes.aclSuperUser) && <Typography>{loginSuffix}</Typography>}
            </Stack>
          )}
          {loginMethod === "name" && (
            <Stack>
              <Typography variant="body2" color="text.tier">
                {t("common:preview")}
              </Typography>
              <Typography>
                {newUser.firstName}.{newUser.lastName}
                {loginSuffix}
              </Typography>
            </Stack>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mt: 3, mb: 3 }} />

      <Box>
        <Stack spacing={1.5}>
          <Typography variant="h5">{t("modal:body.customerLinks")}</Typography>
          <Typography mt={0.5} color="text.tier">
            {t("modal:body.customerLinksHelpText")}
          </Typography>

          <Stack direction="row" alignItems="center">
            {customers && (
              <Autocomplete
                value={customersSelected}
                multiple
                onChange={(e, value) => setCustomersSelected(value)}
                getOptionLabel={(option) => "#" + option.id.toString() + " " + option.name || ""}
                options={customers}
                sx={{ width: "100%" }}
                id={"customerLinks"}
                renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                isOptionEqualToValue={(option, value) => option === value || false}
              />
            )}
          </Stack>
        </Stack>
      </Box>
      <Divider sx={{ mt: 3, mb: 3 }} />
      <Box>
        <Stack spacing={1.5}>
          <Typography variant="h5">{t("modal:body.assignTo")}</Typography>
          <Typography mt={0.5} color="text.tier">
            {t("modal:body.assignToHelpText")}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1}>
            {groupList && (
              <Autocomplete
                disablePortal
                options={groupList}
                renderTags={() => null} // If is controlled, render tags below
                sx={{ flex: 1 }}
                popupIcon={<ExpandMoreRoundedIcon />}
                getOptionLabel={(option) => option.group?.name || ""}
                onChange={(e, value) => {
                  setGroupSelected(value ? value : undefined);
                  setNewUser(
                    produce((draft) => {
                      draft!.groupId = value?.id!;
                    })
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.searchExistingGroup")}
                    sx={{ width: "100%" }}
                  />
                )}
              />
            )}
            <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={handleOpenModalNewGroup}>
              {t("common:newGroup")}
            </Button>
          </Stack>
          {groupSelected && (
            <Stack direction="row" flexWrap="wrap">
              <UserGroupChip roleId={groupSelected.group?.roleId} roles={roleList} sx={{ mr: 1, mb: 1 }} />
            </Stack>
          )}
        </Stack>
      </Box>
    </Modal>
  );
});

export default ModalAddUser;
